/* Training Bundles admin panel.
 *
 * One row per bundle from the manifest. Click "Generate" to kick a
 * background job that records the UI walkthrough + narration and
 * stores the MP4 in Object Storage. Click "Preview" to play any
 * finished bundle in a modal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, PlayCircle, Video, Sparkles, RefreshCw, ExternalLink, Youtube } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function StatusPill({ status }) {
  const map = {
    queued:  { label: 'Queued',  cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-200' },
    running: { label: 'Recording', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-200' },
    done:    { label: 'Ready',   cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200' },
    error:   { label: 'Failed',  cls: 'bg-red-500/15 text-red-700 dark:text-red-200' },
  };
  const m = map[status] || { label: status, cls: 'bg-slate-500/15 text-slate-700' };
  return (
    <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.cls}`}
          data-testid={`training-bundle-status-${status}`}>
      {m.label}
    </span>
  );
}

export default function TrainingBundlesPanel() {
  const [state, setState]     = useState({ catalog: [], persisted: [], jobs: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState({}); // key -> true while POSTing
  const [preview, setPreview] = useState(null); // { key, title, url }

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/training-bundles`, { withCredentials: true });
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bundles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [refresh]);

  const persistedByKey = useMemo(() => {
    const out = {};
    for (const row of state.persisted || []) out[row.key] = row;
    return out;
  }, [state.persisted]);

  const latestJobByKey = useMemo(() => {
    const out = {};
    for (const j of state.jobs || []) {
      const cur = out[j.key];
      if (!cur || (j.queued_at || '') > (cur.queued_at || '')) out[j.key] = j;
    }
    return out;
  }, [state.jobs]);

  const generate = async (key) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await axios.post(`${API}/admin/training-bundles/${key}/generate`, {},
                                    { withCredentials: true });
      toast.success(`Queued: ${r.data.job_id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue generation.');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const generateAll = async () => {
    if (!window.confirm('Queue all 15 bundles? Estimated total cost ≈ $3-4 in Emergent LLM credit and ~60-90 minutes of pod time.')) return;
    try {
      const r = await axios.post(`${API}/admin/training-bundles/generate-all`, {},
                                    { withCredentials: true });
      toast.success(`Queued ${r.data.count} bundles.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue all bundles.');
    }
  };

  const uploadToYouTube = async (key) => {
    if (!state.youtube_configured) {
      toast.error('YouTube OAuth not configured. Set YOUTUBE_OAUTH_CLIENT_ID / _SECRET / _REFRESH_TOKEN in Emergent Deployment Secrets first.');
      return;
    }
    if (!window.confirm(`Upload ${key} to YouTube as UNLISTED?\nThe URL will automatically bind to matching HelpTip popups.`)) return;
    setBusy((b) => ({ ...b, [`yt:${key}`]: true }));
    try {
      const r = await axios.post(`${API}/admin/training-bundles/${key}/upload-youtube`,
                                    { privacy: 'unlisted', bind_helptips: true },
                                    { withCredentials: true });
      toast.success(`Uploaded: ${r.data.url} · bound ${r.data.helptip_slugs_bound?.length || 0} HelpTip slots`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'YouTube upload failed.');
    } finally {
      setBusy((b) => ({ ...b, [`yt:${key}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="training-bundles-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalGenerated = Object.keys(persistedByKey).length;
  const totalBundles   = (state.catalog || []).length;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4" data-testid="training-bundles-panel">
      <div className="flex items-start gap-2">
        <Video className="mt-0.5 h-5 w-5 text-primary" />
        <div className="flex-1">
          <h2 className="text-[18px] font-semibold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Training Video Bundles · AI narration + UI walkthroughs
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Each bundle records a 3–5 min tutorial: OpenAI TTS narrates while
            Playwright drives the actual UI. Estimated cost per bundle ~$0.20.
            Videos land in Emergent Object Storage and auto-bind to the matching
            HelpTip once generated.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-panel-header/40 p-2">
        <div className="text-[11.5px] text-muted-foreground"
             data-testid="training-bundles-stats">
          <b className="text-foreground">{totalGenerated}</b> / {totalBundles} bundles generated
          {' · '}
          <span className={state.youtube_configured
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-amber-600 dark:text-amber-400'}
             data-testid="training-bundles-yt-status">
            YouTube: {state.youtube_configured ? 'connected' : 'not configured'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  className="h-8 gap-1"
                  data-testid="training-bundles-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={generateAll}
                  className="h-8 gap-1"
                  data-testid="training-bundles-generate-all">
            <Sparkles className="h-3.5 w-3.5" />
            Generate all
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-card">
        <div className="divide-y divide-border">
          {(state.catalog || []).map((row) => {
            const persisted = persistedByKey[row.key];
            const job = latestJobByKey[row.key];
            const url = persisted?.url;
            const status = job?.status;
            return (
              <div key={row.key}
                   className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]"
                   data-testid={`training-bundle-row-${row.key}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground">
                    {row.title}
                    {status && <StatusPill status={status} />}
                  </div>
                  <div className="text-[10.5px] text-muted-foreground font-mono-stat">
                    {row.key} · {row.step_count} steps · ~{Math.round(row.est_seconds / 60)} min
                  </div>
                  <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                    {row.covers}
                  </p>
                  {job?.error && (
                    <p className="mt-1 text-[10.5px] text-red-600" data-testid={`training-bundle-error-${row.key}`}>
                      {job.error}
                    </p>
                  )}
                </div>
                <div className="min-w-0 text-[11px] text-muted-foreground">
                  {persisted?.updated_at && (
                    <div>Updated: {new Date(persisted.updated_at).toLocaleString()}</div>
                  )}
                  {persisted?.cost && (
                    <div
                      className="mt-0.5 inline-flex items-center gap-1 rounded-sm bg-primary/10 px-1.5 py-0.5 font-mono-stat text-[10.5px] text-primary"
                      data-testid={`training-bundle-cost-${row.key}`}
                      title={`TTS $${(persisted.cost.tts_usd || 0).toFixed(3)} · Compute $${(persisted.cost.compute_usd || 0).toFixed(3)} · ${persisted.cost.tts_model} / ${persisted.cost.voice}`}
                    >
                      ${(persisted.cost.cost_usd || 0).toFixed(2)} · {persisted.cost.cost_ecu || 0} ECU
                    </div>
                  )}
                  {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                       className="mt-0.5 inline-flex items-center gap-1 truncate text-primary hover:underline"
                       data-testid={`training-bundle-url-${row.key}`}>
                      <ExternalLink className="h-3 w-3" />
                      {url.replace(/^https?:\/\//, '').slice(0, 42)}…
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <Button size="sm" variant="outline" className="h-8 gap-1"
                          onClick={() => generate(row.key)}
                          disabled={busy[row.key] || status === 'running' || status === 'queued'}
                          data-testid={`training-bundle-generate-${row.key}`}>
                    {busy[row.key]
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Sparkles className="h-3.5 w-3.5" />}
                    {persisted ? 'Regenerate' : 'Generate'}
                  </Button>
                  {url && (
                    <Button size="sm" variant="outline" className="h-8 gap-1"
                            onClick={() => setPreview({ key: row.key, title: row.title, url })}
                            data-testid={`training-bundle-preview-${row.key}`}>
                      <PlayCircle className="h-3.5 w-3.5" />
                      Preview
                    </Button>
                  )}
                  {url && (
                    <Button size="sm" variant="outline" className="h-8 gap-1"
                            onClick={() => uploadToYouTube(row.key)}
                            disabled={busy[`yt:${row.key}`] || !state.youtube_configured}
                            data-testid={`training-bundle-youtube-${row.key}`}>
                      {busy[`yt:${row.key}`]
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Youtube className="h-3.5 w-3.5 text-red-500" />}
                      YouTube
                    </Button>
                  )}
                  {persisted?.youtube_url && (
                    <a href={persisted.youtube_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex h-8 items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
                       data-testid={`training-bundle-youtube-url-${row.key}`}>
                      <ExternalLink className="h-3 w-3" />
                      Live
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
             onClick={() => setPreview(null)}
             data-testid="training-bundle-preview-overlay">
          <div className="w-full max-w-3xl overflow-hidden rounded-md border border-border bg-card"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border bg-panel-header/40 px-3 py-2">
              <div className="text-[13px] font-semibold">{preview.title}</div>
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)}
                      className="h-7 text-[11px]"
                      data-testid="training-bundle-preview-close">
                Close
              </Button>
            </div>
            <video src={preview.url} controls autoPlay
                   className="w-full max-h-[70vh] bg-black"
                   data-testid={`training-bundle-preview-video-${preview.key}`} />
          </div>
        </div>
      )}
    </div>
  );
}
